import { Link } from "react-router-dom";
import { ITicket } from "../models/ITicket";
import "../../styles/style.css";

const PRIORITY: Record<number, { label: string; color: string; bg: string }> = {
  1: { label: "Düşük",   color: "#16a34a", bg: "#f0fdf4" },
  2: { label: "Orta",    color: "#2563eb", bg: "#eff6ff" },
  3: { label: "Yüksek",  color: "#ea580c", bg: "#fff7ed" },
  4: { label: "Kritik",  color: "#dc2626", bg: "#fef2f2" },
};

const STATUS: Record<string, { color: string; bg: string; border: string }> = {
  "Açık":               { color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0" },
  "Ticket yaratıldı":   { color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe" },
  "Kapalı":             { color: "#6b7280", bg: "#f9fafb", border: "#e5e7eb" },
  "Beklemede":          { color: "#d97706", bg: "#fffbeb", border: "#fde68a" },
};

function getStatus(desc: string) {
  return STATUS[desc] ?? { color: "#6b7280", bg: "#f9fafb", border: "#e5e7eb" };
}

function timeAgo(dateStr: string): string {
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60)   return "Az önce";
  if (diff < 3600) return `${Math.floor(diff / 60)} dk önce`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} sa önce`;
  return `${Math.floor(diff / 86400)} gün önce`;
}

const BEKLEMEDE_STATUS_ID  = "fb939613-6149-4799-9837-a6ae823b8983";
const TAMAMLANAN_STATUS_ID = "b69b62f7-3800-458a-bcc0-e1a72bc7a9b8";

export default function TicketCard({ ticket, currentUserId, currentUserDeptIds = [], onTake, compact = false }: {
  ticket: ITicket;
  currentUserId?: string;
  currentUserDeptIds?: string[];
  onTake?: (ticketId: string) => void;
  compact?: boolean;
}) {
  const priority   = PRIORITY[ticket.priority] ?? PRIORITY[1];
  const status     = getStatus(ticket.ticketStatusDescription);
  const isKritik   = ticket.priority === 4;

  if (compact) {
    const preview = ticket.note?.trim()
      || ticket.messages?.[0]?.message?.trim()
      || "";
    return (
      <Link
        to={`/ticket/${ticket.id}`}
        style={{
          display: "flex", background: "#fff",
          borderRadius: 14, overflow: "hidden",
          border: isKritik ? "1.5px solid #fca5a5" : "1.5px solid #f1f5f9",
          boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
          cursor: "pointer", height: 120,
          transition: "box-shadow 0.18s, transform 0.18s",
          marginBottom: 0,
          textDecoration: "none", color: "inherit",
        }}
        onMouseEnter={e => {
          (e.currentTarget as HTMLAnchorElement).style.boxShadow = "0 4px 20px rgba(0,0,0,0.12)";
          (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(-2px)";
        }}
        onMouseLeave={e => {
          (e.currentTarget as HTMLAnchorElement).style.boxShadow = "0 1px 6px rgba(0,0,0,0.06)";
          (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(0)";
        }}
      >
        {/* Priority stripe */}
        <div style={{ width: isKritik ? 7 : 5, background: priority.color, flexShrink: 0 }} />

        <div style={{ padding: "10px 14px", flex: 1, minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
          {/* Top row: ticketNo + status */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: "0.68rem", fontWeight: 700, color: "#6366f1", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {ticket.ticketNo ? <><i className="bi bi-hash"></i>{ticket.ticketNo}</> : null}
            </span>
            <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
              {!!ticket.taskCount && (
                <span style={{
                  fontSize: "0.62rem", fontWeight: 700, padding: "2px 7px",
                  borderRadius: 20, background: "#eef2ff", color: "#4f46e5", whiteSpace: "nowrap",
                }}>
                  <i className="bi bi-list-check"></i> %{ticket.progressPercent ?? 0}
                </span>
              )}
              <span style={{
                fontSize: "0.65rem", fontWeight: 700, padding: "2px 8px",
                borderRadius: 20, background: status.bg, color: status.color,
                border: `1px solid ${status.border}`, whiteSpace: "nowrap",
              }}>
                {ticket.ticketStatusDescription}
              </span>
            </div>
          </div>

          {/* Title */}
          <div style={{
            fontWeight: 700, fontSize: "0.88rem", color: "#111827",
            overflow: "hidden", display: "-webkit-box",
            WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
            lineHeight: 1.35,
          }}>
            {ticket.title}
          </div>

          {/* First message preview */}
          <div style={{
            fontSize: "0.74rem", color: "#9ca3af", marginTop: 4,
            overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis",
          }}>
            {preview || <span style={{ fontStyle: "italic" }}>Not yok</span>}
          </div>
        </div>
      </Link>
    );
  }

  const showCloseWarning =
    !!currentUserId &&
    ticket.mainCustomerId === currentUserId &&
    ticket.ticketStatusId === TAMAMLANAN_STATUS_ID;

  const showTakeButton =
    !!currentUserId &&
    ticket.ticketStatusId === BEKLEMEDE_STATUS_ID &&
    !ticket.customerId?.trim() &&
    currentUserDeptIds.includes(ticket.ticketDepartmentId);

  return (
    <Link
      to={`/ticket/${ticket.id}`}
      className={isKritik ? "ticket-card--kritik" : ""}
      style={{
        display: "flex",
        background: ticket.ticketStatusId === BEKLEMEDE_STATUS_ID ? "#fff7ed" : "#fff",
        borderRadius: "14px",
        border: ticket.ticketStatusId === BEKLEMEDE_STATUS_ID ? "1.5px solid #fed7aa" : "1.5px solid #f1f5f9",
        boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
        cursor: "pointer",
        overflow: "hidden",
        transition: "box-shadow 0.18s, transform 0.18s",
        marginBottom: "12px",
        textDecoration: "none", color: "inherit",
      }}
      onMouseEnter={e => {
        (e.currentTarget as HTMLAnchorElement).style.boxShadow = "0 4px 20px rgba(0,0,0,0.12)";
        (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(-2px)";
      }}
      onMouseLeave={e => {
        (e.currentTarget as HTMLAnchorElement).style.boxShadow = "0 1px 6px rgba(0,0,0,0.06)";
        (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(0)";
      }}
    >
      {/* Priority stripe */}
      <div style={{ width: isKritik ? "7px" : "5px", background: priority.color, flexShrink: 0 }} />

      {/* Content */}
      <div style={{ padding: "14px 18px", flex: 1, minWidth: 0 }}>
        {/* Konu */}
        {ticket.ticketSubjectName && (
          <div style={{ marginBottom: "8px", fontSize: "0.72rem", fontWeight: 700, color: "#7c3aed", display: "flex", alignItems: "center", gap: "4px" }}>
            <i className="bi bi-tag-fill"></i>{ticket.ticketSubjectName}
          </div>
        )}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Ticket No + Title */}
            {ticket.ticketNo && (
              <div style={{ fontSize: "0.7rem", fontWeight: 700, color: "#6366f1", marginBottom: "2px", letterSpacing: "0.03em" }}>
                <i className="bi bi-hash me-1"></i>{ticket.ticketNo}
              </div>
            )}
            <div style={{ fontWeight: 700, fontSize: "0.97rem", color: "#111827", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {ticket.title}
            </div>
            {/* Customer + Department */}
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "5px", flexWrap: "wrap" }}>
              <span style={{ fontSize: "0.78rem", color: "#6b7280" }}>
                <i className="bi bi-person-fill me-1"></i>{ticket.mainCustomerName}
              </span>
              {ticket.customerName && (
                <span style={{ fontSize: "0.78rem", fontWeight: 700, color: "#374151" }}>
                  <i className="bi bi-person-check-fill me-1" style={{ color: "#6366f1" }}></i>{ticket.customerName}
                </span>
              )}
              <span style={{
                fontSize: "0.72rem", fontWeight: 600, padding: "2px 8px",
                borderRadius: "20px", background: "#f1f5f9", color: "#475569",
              }}>
                <i className="bi bi-diagram-3 me-1"></i>{ticket.ticketDepartmentName}
              </span>
            </div>
            {/* Note preview */}
            {ticket.note && (
              <div style={{ marginTop: "6px", fontSize: "0.8rem", color: "#9ca3af", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {ticket.note}
              </div>
            )}
            {/* Görev ilerlemesi */}
            {!!ticket.taskCount && (
              <div style={{ marginTop: "8px", maxWidth: "260px" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "3px" }}>
                  <span style={{ fontSize: "0.72rem", fontWeight: 600, color: "#6b7280" }}>
                    <i className="bi bi-list-check me-1"></i>
                    {ticket.completedTaskCount ?? 0}/{ticket.taskCount} görev
                  </span>
                  <span style={{ fontSize: "0.72rem", fontWeight: 800, color: "#4f46e5" }}>%{ticket.progressPercent ?? 0}</span>
                </div>
                <div style={{ width: "100%", height: "5px", borderRadius: "20px", background: "#e5e7eb", overflow: "hidden" }}>
                  <div style={{
                    width: `${Math.min(100, Math.max(0, ticket.progressPercent ?? 0))}%`, height: "100%",
                    background: "linear-gradient(90deg, #6366f1, #4f46e5)", borderRadius: "20px",
                  }} />
                </div>
              </div>
            )}
          </div>

          {/* Right side */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "6px", flexShrink: 0 }}>
            {/* Kritik badge */}
            {isKritik && (
              <span className="ticket-kritik-badge">
                <i className="bi bi-exclamation-triangle-fill"></i> KRİTİK
              </span>
            )}
            {/* Status */}
            <span style={{
              fontSize: "0.72rem", fontWeight: 700, padding: "3px 10px",
              borderRadius: "20px", background: status.bg, color: status.color,
              border: `1px solid ${status.border}`,
            }}>
              {ticket.ticketStatusDescription}
            </span>
            {/* Priority — kritik için ayrıca gösterme, zaten badge var */}
            {!isKritik && (
              <span style={{
                fontSize: "0.7rem", fontWeight: 600, padding: "2px 8px",
                borderRadius: "20px", background: priority.bg, color: priority.color,
              }}>
                {priority.label}
              </span>
            )}
          </div>
        </div>

        {/* Beklemede uyarısı */}
        {ticket.ticketStatusId === BEKLEMEDE_STATUS_ID && ticket.customerName?.trim() && (
          <div className="ticket-beklemede-warning">
            <i className="bi bi-hourglass-split"></i>
            <span>{ticket.customerName.split(" ")[0]} Tarafından Kontrol Edilmesi Bekleniyor</span>
          </div>
        )}

        {/* Kapat uyarısı */}
        {showCloseWarning && (
          <div style={{
            marginTop: "10px", padding: "8px 12px", borderRadius: "10px",
            background: "#fef9c3", border: "1px solid #fde68a",
            display: "flex", alignItems: "center", gap: "6px",
          }}>
            <i className="bi bi-check-circle-fill" style={{ color: "#d97706", flexShrink: 0 }}></i>
            <span style={{ fontSize: "0.78rem", fontWeight: 600, color: "#92400e" }}>
              Tamamlandı — ticketi kapatmak ister misiniz?
            </span>
          </div>
        )}

        {/* Footer */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "10px" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ fontSize: "0.75rem", color: "#9ca3af" }}>
              <i className="bi bi-clock me-1"></i>{timeAgo(ticket.createdAt)}
            </span>
            {ticket.updatedAt && ticket.updatedAt !== ticket.createdAt && (
              <span style={{ fontSize: "0.72rem", color: "#6366f1" }}>
                <i className="bi bi-arrow-clockwise me-1"></i>Son güncelleme: {timeAgo(ticket.updatedAt)}
              </span>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {showTakeButton && (
              <button
                onClick={(e) => { e.stopPropagation(); onTake?.(ticket.id); }}
                style={{
                  fontSize: "0.72rem", fontWeight: 700,
                  padding: "3px 10px", borderRadius: "20px", border: "none",
                  background: "linear-gradient(135deg, #6366f1, #4f46e5)",
                  color: "#fff", cursor: "pointer",
                  display: "flex", alignItems: "center", gap: "4px",
                  boxShadow: "0 2px 8px rgba(99,102,241,0.35)",
                }}
              >
                <i className="bi bi-person-check-fill"></i> Ticketi Al
              </button>
            )}
            <span style={{
              fontSize: "0.75rem", fontWeight: 600, color: ticket.messageCount > 0 ? "#2563eb" : "#9ca3af",
              display: "flex", alignItems: "center", gap: "4px",
            }}>
              <i className="bi bi-chat-dots-fill"></i>
              {ticket.messageCount} mesaj
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}
